import { useMutation, useQuery } from '@apollo/client'
import {
  Container,
  Divider,
  ListItem,
  ListItemText,
  Paper,
  Stack,
} from '@mui/material'
import { SectionHeader } from '@sitoo/mui-components'
import { useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  GetNetsBaxiLogsPluginDocument,
  PluginNetsBaxiLogs,
  PluginNetsBaxiLogsPathEnum,
  UpdateNetsBaxiLogsDocument,
} from '../../../../generated/graphql'
import { SelectInput } from '../../../../inputs/select-input'
import { SwitchInput } from '../../../../inputs/switch-input'
import { TextFieldInput } from '../../../../inputs/text-field-input'
import {
  AuthenticationDialog,
  AuthenticationDialogForm,
} from '../authentication/dialog'
import { AuthenticationSection } from '../authentication/section'
import { PluginsForm } from '../plugins-form'

type Form = PluginNetsBaxiLogs & {
  use_s3_compatible_service: boolean
}

export const SettingsNetsBaxiLogsPage = () => {
  const { t } = useTranslation(['apps', 'shared'])

  const { data } = useQuery(GetNetsBaxiLogsPluginDocument, {
    fetchPolicy: 'cache-and-network',
  })

  const [updateSettings, { loading: isLoading }] = useMutation(
    UpdateNetsBaxiLogsDocument,
  )

  const formContext = useForm<Form>({
    defaultValues: {
      region: '',
      bucket: '',
      path: null,
      use_s3_compatible_service: false,
      url: '',
      access_key_id: '',
      access_key_secret: '',
    },
  })

  const { control, getValues, formState } = formContext

  const [isAuthOpen, setAuthOpen] = useState(false)
  const isAuthConfigured = Boolean(
    getValues('access_key_id') && getValues('access_key_secret'),
  )

  const onSubmit = async (formData: Form) => {
    const { use_s3_compatible_service, ...rest } = formData

    const dataToSubmit = {
      ...rest,
      url: use_s3_compatible_service ? rest.url : '',
    }

    const { data } = await updateSettings({
      variables: {
        data: dataToSubmit,
      },
    })

    return {
      ...data?.updateNetsBaxiLogs,
      use_s3_compatible_service: Boolean(data?.updateNetsBaxiLogs?.url),
    }
  }

  const onAuthSubmit = async (formData: AuthenticationDialogForm) => {
    const { login, password } = formData

    if (!data?.netsBaxiLogsPlugin) return

    await onSubmit({
      access_key_id: login,
      access_key_secret: password,
    } as Form)
  }

  const pathOptions = useMemo(
    () => [
      {
        value: null,
        name: t('apps:nets_baxi_logs.path_default'),
      },
      ...Object.values(PluginNetsBaxiLogsPathEnum).map((value) => ({
        value,
        name: t(`apps:nets_baxi_logs.path_${value.toLowerCase()}`),
      })),
    ],
    [t],
  )

  const serverData = useMemo(() => {
    return {
      ...data?.netsBaxiLogsPlugin,
      use_s3_compatible_service: Boolean(data?.netsBaxiLogsPlugin?.url),
    }
  }, [data?.netsBaxiLogsPlugin])

  return (
    <Container>
      <AuthenticationDialog
        open={isAuthOpen}
        onClose={() => setAuthOpen(false)}
        onSubmit={onAuthSubmit}
        defaultValues={{
          login: data?.netsBaxiLogsPlugin.access_key_id,
        }}
        slotProps={{
          loginInput: {
            label: t('apps:nets_baxi_logs.access_key_id_label'),
            helperText: t('apps:nets_baxi_logs.access_key_id_helper'),
          },
          passwordInput: {
            label: t('apps:nets_baxi_logs.access_key_secret_label'),
            helperText: t('apps:nets_baxi_logs.access_key_secret_helper'),
          },
        }}
      />
      <FormProvider {...formContext}>
        <PluginsForm
          serverData={serverData}
          onSubmit={onSubmit}
          submitLabel={t('shared:action.save')}
          isLoading={isLoading}
        >
          <Paper sx={{ mb: 2 }}>
            <SectionHeader variant="transparent">
              {t('apps:nets_baxi_logs.s3_file_upload')}
            </SectionHeader>

            <Stack gap={2} sx={{ p: 2, pt: 1 }}>
              <TextFieldInput
                name="region"
                control={control}
                label={t('apps:nets_baxi_logs.region_label')}
                helperText={t('apps:nets_baxi_logs.region_helper')}
              />

              <TextFieldInput
                name="bucket"
                control={control}
                label={t('apps:nets_baxi_logs.bucket_label')}
                helperText={t('apps:nets_baxi_logs.bucket_helper')}
              />

              <SelectInput
                name="path"
                control={control}
                label={t('apps:nets_baxi_logs.path_label')}
                helperText={t('apps:nets_baxi_logs.path_helper')}
                options={pathOptions}
                slotProps={{ select: { displayEmpty: true } }}
              />
            </Stack>
            <Divider />
            <ListItem
              component="div"
              secondaryAction={
                <SwitchInput
                  control={control}
                  name="use_s3_compatible_service"
                  aria-label={t(
                    'apps:nets_baxi_logs.use_s3_compatible_service_label',
                  )}
                />
              }
            >
              <ListItemText
                primary={t(
                  'apps:nets_baxi_logs.use_s3_compatible_service_label',
                )}
                secondary={t(
                  'apps:nets_baxi_logs.use_s3_compatible_service_helper',
                )}
              />
            </ListItem>

            {formContext.watch('use_s3_compatible_service') && (
              <>
                <Divider />
                <Stack gap={2} sx={{ p: 2, pt: 1 }}>
                  <TextFieldInput
                    name="url"
                    control={control}
                    label={t('apps:nets_baxi_logs.url_label')}
                    helperText={t('apps:nets_baxi_logs.url_helper')}
                    required
                  />
                </Stack>
              </>
            )}
          </Paper>

          <AuthenticationSection
            isConfigured={isAuthConfigured}
            disabled={formState.isDirty}
            onClick={() => setAuthOpen(true)}
            isLoading={isLoading}
          />
        </PluginsForm>
      </FormProvider>
    </Container>
  )
}
